import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { GAME_STATUS, OUTRIGHT_TYPES } from '@sportsbook/constants';

import { SportType, TournamentType, MatchType, CategoryType } from '../../api/types';
import { BreadCrumbs } from '../../components/bread-crumbs/bread-crumbs';
import { MAX_TOP_SPORTS } from '../../constants';
import { useApi } from '../../hooks/api-hooks';
import { useSportsbookApi } from '../../hooks/use-sportsbook-api';
import {
  getCrumbItems,
  BREADCRUMB_TYPES,
  sportToBreadcrumbDropdownItem,
  leagueToBreadcrumbDropdownItem,
  gameToBreadcrumbDropdownItem,
  categoryToBreadcrumbDropdownItem,
} from '../../utils/bread-crumbs';

const LIVE_STATUS = [GAME_STATUS.LIVE];

async function fetchBreadCrumbsDropdownData(
  sbApi,
  { type, sportKey, leagueId, locale, sportType, isLive },
  axiosConfig
) {
  if (type === BREADCRUMB_TYPES.SPORTSLIST) {
    return sbApi
      .fetchAllSportsWithCounts(
        { locale, isBettable: true, sportType, matchStatus: isLive ? LIVE_STATUS : undefined },
        axiosConfig
      )
      .then(sports => {
        const sportsWithCounts = sports
          .filter(sport => sport.gamesCount + sport.outrightsCount > 0)
          .map(sportToBreadcrumbDropdownItem);
        return [
          ...sportsWithCounts.slice(0, MAX_TOP_SPORTS),
          ...sportsWithCounts
            .slice(MAX_TOP_SPORTS)
            .sort((a, b) => a.data.sportName.localeCompare(b.data.sportName, locale)),
        ];
      });
  }

  if (type === BREADCRUMB_TYPES.OUTRIGHTSLIST) {
    return sbApi
      .fetchAllCategoriesWithCounts({ sportKey, locale, type: OUTRIGHT_TYPES }, axiosConfig)
      .then(categories => {
        return categories
          .filter(category => category.gamesCount + category.outrightsCount > 0)
          .map(categoryToBreadcrumbDropdownItem);
      });
  }

  if (type === BREADCRUMB_TYPES.LEAGUESLIST) {
    return sbApi
      .fetchAllTournamentsWithCounts({ sportKey, locale, matchStatus: isLive ? LIVE_STATUS : undefined }, axiosConfig)
      .then(leagues =>
        leagues.filter(league => league.gamesCount + league.outrightsCount > 0).map(leagueToBreadcrumbDropdownItem)
      );
  }

  if (type === BREADCRUMB_TYPES.MATCHESLIST) {
    return sbApi
      .getMatches({ leagueId, locale, isBettable: true, status: isLive ? LIVE_STATUS : undefined }, axiosConfig)
      .then(({ data: matches }) => matches.map(gameToBreadcrumbDropdownItem));
  }

  return [];
}

export const BreadCrumbsContainer = ({ activeObject }) => {
  const { locale } = useIntl();
  const pathItems = useMemo(() => getCrumbItems(activeObject), [
    activeObject.sportId,
    activeObject.leagueId,
    activeObject.leagueSlug,
    activeObject.matchId,
    activeObject.matchSlug,
    activeObject.categoryId,
  ]);

  const sbApi = useSportsbookApi();
  const fetchData = useApi(fetchBreadCrumbsDropdownData);
  const loadData = useCallback(
    type =>
      fetchData(sbApi, {
        type,
        sportKey: activeObject.sportKey,
        sportType: activeObject.sportType,
        leagueId: activeObject.leagueId,
        locale,
        isLive: activeObject.isLive,
      }),
    [
      sbApi,
      activeObject.sportId,
      activeObject.sportKey,
      activeObject.sportType,
      activeObject.leagueId,
      activeObject.matchId,
      locale,
    ]
  );

  return <BreadCrumbs isLive={activeObject.isLive} loadData={loadData} pathItems={pathItems} />;
};

BreadCrumbsContainer.propTypes = {
  activeObject: PropTypes.oneOfType([SportType, TournamentType, MatchType, CategoryType]).isRequired,
};
